/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import type { FC } from 'react';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl-next';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { fg } from '@confluence/feature-gating';

import { PRIMARY_COLORS } from '../colors';
import { colorCodeToColorName, tokenToHex } from '../helpers';
import { ContentStateRestrictionLevel } from '../ContentStateRestrictionLevel';

import type {
	EditorContentStateComponentSetContentStateMutation as EditorContentStateComponentSetContentStateMutation$data,
	EditorContentStateComponentSetContentStateMutationVariables as EditorContentStateComponentSetContentStateMutation$variables,
} from './__types__/EditorContentStateComponentSetContentStateMutation';
import type {
	EditorContentStateComponentRemoveContentStateMutation as EditorContentStateComponentRemoveContentStateMutation$data,
	EditorContentStateComponentRemoveContentStateMutationVariables as EditorContentStateComponentRemoveContentStateMutation$variables,
} from './__types__/EditorContentStateComponentRemoveContentStateMutation';
import { ContentStateButton } from './ContentStateButton';
import { ContentStateMenu } from './ContentStateMenu';
import type { EditorContentStateProps } from './types';
import { createNewCustomStatesArrayWhenNewCustomAdded, isDuplicate } from './helpers';
import { i18n } from './i18n';
import { EditorContentStateQuery } from './EditorContentStateQuery.graphql';

export const EditorContentStateComponentImpl: FC<EditorContentStateProps> = ({
	contentId,
	contentType,
	contentStateState,
	userOffline,
	flags,
	forceContentStatePlacementToTop,
	onUpdateSuccess,
	onContentStateMenuToggle,
	floatingToolbarButtonStyle,
	renderPlusButtonEmptyState,
	isDetailsPanel,
	onClick,
}) => {
	// need this ref to store original content state in case of mutation error and we need to rollback to the original one
	const originalContentState = useRef(contentStateState.state.contentState);
	const contentStateButtonRef = useRef(null);
	const [isCreatingContentState, setIsCreatingContentState] = useState<boolean>(false);
	const [selectedColor, setSelectedColor] = useState<string | null>(null);
	const [isColorPickerOpen, setIsColorPickerOpen] = useState<boolean>(false);
	const [isHoveringRemove, setIsHoveringRemove] = useState<boolean>(false);
	const [isContentStateMenuOpen, setIsContentStateMenuOpen] = useState<boolean>(false);
	const setIsContentStateMenuOpenWithListener = useCallback(
		(newValue: boolean) => {
			onContentStateMenuToggle?.(newValue);
			setIsContentStateMenuOpen(newValue);
		},
		[onContentStateMenuToggle, setIsContentStateMenuOpen],
	);
	const isLivePage = useIsCurrentPageLive();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [shouldSetAutomationFlag, setShouldSetAutomationFlag] = useState(false);
	useEffect(() => {
		if (
			!localStorage?.getItemAsBoolean(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE) &&
			fg('cc_a4c_smart_button_changeboarding')
		) {
			setShouldSetAutomationFlag(true);
		}
	}, []);
	const [removeContentStateMutation] = useMutation<
		EditorContentStateComponentRemoveContentStateMutation$data,
		EditorContentStateComponentRemoveContentStateMutation$variables
	>(
		gql`
			mutation EditorContentStateComponentRemoveContentStateMutation($contentId: ID!) {
				removeContentState(contentId: $contentId)
			}
		`,
		{
			// Need to refetch since our cache isn't updating correctly - temporary solution until BE fixes
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
					},
				},
			],
			onCompleted: () => {
				onUpdateSuccess?.();
			},
		},
	);
	const [setContentStateMutation] = useMutation<
		EditorContentStateComponentSetContentStateMutation$data,
		EditorContentStateComponentSetContentStateMutation$variables
	>(
		gql`
			mutation EditorContentStateComponentSetContentStateMutation(
				$contentId: ID!
				$contentStateInput: ContentStateInput!
			) {
				setContentState(contentId: $contentId, contentState: $contentStateInput) {
					name
					color
					id
					restrictionLevel
				}
			}
		`,
		{
			onError: () => {
				void contentStateState.setContentState(originalContentState.current);
				void flags.showErrorFlag({
					id: 'content-state-error-flag',
					title: <FormattedMessage {...i18n.errorFlagTitle} />,
					description: <FormattedMessage {...i18n.errorFlagBody} />,
				});
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						objectId: contentId,
						name: 'contentStateErrorFlag',
						attributes: {
							isLivePage,
						},
					},
				}).fire();
			},
			onCompleted: (data) => {
				// In this file if this mutation is called, it means a user created a new custom content state. We don't know this new
				// content state's ID since it's newly created so we need to wait on the BE mutation response.
				// Here, we create a new custom states array to add the newly added custom states since that list always has the
				// 3 most recently used states.
				if (data.setContentState) {
					const newCustomStatesArray = createNewCustomStatesArrayWhenNewCustomAdded(
						contentStateState.state.customStatesArray,
						data.setContentState,
					);
					void contentStateState.setCustomStatesArray(newCustomStatesArray);
					// @ts-ignore
					originalContentState.current = data.setContentState;
				}
				void contentStateState.setIsUpdating(false);
				onUpdateSuccess?.();
			},
			// Need to refetch since our cache isn't updating correctly - temporary solution until BE fixes
			refetchQueries: () => [
				{
					query: EditorContentStateQuery,
					variables: {
						contentId,
						isDraft: !isLivePage,
					},
				},
			],
		},
	);
	const handleSelectColor = useCallback((newColor: string) => {
		setSelectedColor(newColor);
		setIsColorPickerOpen(false);
		setIsCreatingContentState(true);
	}, []);
	const handleSubmit = useCallback(
		(name: string) => {
			if (
				selectedColor &&
				!isDuplicate(contentStateState.state.contentState, {
					color: selectedColor.toLowerCase(),
					name,
				})
			) {
				const newContentState = { color: selectedColor, name };
				void contentStateState.setIsUpdating(true);
				void setContentStateMutation({
					variables: {
						contentId,
						contentStateInput: {
							...newContentState,
							color: tokenToHex(selectedColor),
						},
					},
				});
				void contentStateState.setContentState(newContentState);
			}
			if (shouldSetAutomationFlag && fg('cc_a4c_smart_button_changeboarding')) {
				// This flag is used in AutomationMenuButtonWithSmartButtonSpotlight
				// It will check this flag to determine if it should show the spotlight
				localStorage.setItem(keys.AUTOMATION_DISCOVER_SET_CONTENT_STATE, true);
				setShouldSetAutomationFlag(false);
			}
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					objectId: contentId,
					actionSubjectId: 'editorContentStateAdded',
					source: 'editorContentState',
					attributes: {
						type: 'new custom',
						color: colorCodeToColorName(selectedColor),
						// Hard coded since all custom states lack restrictions.
						restrictionLevel: ContentStateRestrictionLevel[ContentStateRestrictionLevel.NONE],
						uiLocation: 'pageTitleSection',
						isLivePage,
					},
				},
			}).fire();
			setIsContentStateMenuOpenWithListener(false);
			setIsCreatingContentState(false);
			setSelectedColor(null);
		},
		[
			contentId,
			contentStateState,
			createAnalyticsEvent,
			selectedColor,
			setContentStateMutation,
			setIsContentStateMenuOpenWithListener,
			isLivePage,
			shouldSetAutomationFlag,
		],
	);
	const handleCancel = useCallback(() => {
		setSelectedColor(null);
		setIsCreatingContentState(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateCancel',
				source: 'editorContentState',
				attributes: {
					isLivePage,
				},
			},
		}).fire();
	}, [contentId, createAnalyticsEvent, isLivePage]);
	const handleRemove = useCallback(() => {
		// check if state exists before mutating since the trash button still shows if the feature is disabled
		// (page might not have a content state)
		if (contentStateState.state.contentState) {
			void removeContentStateMutation({
				variables: {
					contentId,
				},
			});
			setIsCreatingContentState(false);
			setIsHoveringRemove(false);
			void contentStateState.setContentState(null);
			setIsContentStateMenuOpenWithListener(false);
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				objectId: contentId,
				actionSubjectId: 'editorContentStateRemoved',
				source: 'editorContentState',
				attributes: {
					uiLocation: 'pageTitleSection',
					isLivePage,
				},
			},
		}).fire();
	}, [
		contentId,
		contentStateState,
		createAnalyticsEvent,
		removeContentStateMutation,
		setIsCreatingContentState,
		setIsContentStateMenuOpenWithListener,
		isLivePage,
	]);
	const handleFocusTextField = useCallback(() => {
		if (!isCreatingContentState) {
			setIsCreatingContentState(true);
			// Blue is default color
			setSelectedColor(PRIMARY_COLORS.BLUE);
		}
	}, [isCreatingContentState]);
	const handleContentStatePopupClose = useCallback(() => {
		setSelectedColor(null);
		setIsCreatingContentState(false);
		setIsColorPickerOpen(false);
		setIsHoveringRemove(false);
		setIsContentStateMenuOpenWithListener(false);
	}, [setIsContentStateMenuOpenWithListener]);
	const handleColorPickerClose = useCallback(() => {
		setIsColorPickerOpen(false);
	}, []);
	const handleColorPickerToggle = useCallback(() => {
		setIsColorPickerOpen((prevIsColorPickerOpen) => !prevIsColorPickerOpen);
	}, []);
	const handleMouseEnterRemove = useCallback(() => {
		setIsHoveringRemove(true);
	}, []);
	const handleMouseLeaveRemove = useCallback(() => {
		setIsHoveringRemove(false);
	}, []);
	const handlePopupToggle = useCallback(() => {
		setIsContentStateMenuOpenWithListener(!isContentStateMenuOpen);
	}, [isContentStateMenuOpen, setIsContentStateMenuOpenWithListener]);

	const togglePopup = () => {
		handlePopupToggle();
		onClick?.();
	};

	return (
		<Popup
			testId="content-state-popup"
			isOpen={isContentStateMenuOpen}
			placement={forceContentStatePlacementToTop ? 'top-end' : 'bottom-start'}
			zIndex={700}
			trigger={(triggerProps) => (
				<ContentStateButton
					triggerProps={{ ...triggerProps }}
					togglePopup={togglePopup}
					contentState={contentStateState.state.contentState}
					isSelected={isContentStateMenuOpen}
					isWarning={isHoveringRemove}
					userOffline={userOffline}
					contentStateButtonRef={contentStateButtonRef}
					floatingToolbarButtonStyle={floatingToolbarButtonStyle}
					renderPlusButtonEmptyState={renderPlusButtonEmptyState}
					isDetailsPanel={isDetailsPanel}
				/>
			)}
			content={({ update }) => (
				<ContentStateMenu
					contentId={contentId}
					contentStateState={contentStateState}
					contentType={contentType}
					isCustomEnabled={contentStateState.state.isCustomEnabled}
					defaultStatesArray={contentStateState.state.defaultStatesArray || []}
					customStatesArray={contentStateState.state.customStatesArray || []}
					isContentStateEnabled={contentStateState.state.isContentStateEnabled}
					isSuggestedEnabled={contentStateState.state.isSuggestedEnabled}
					isCreatingContentState={isCreatingContentState}
					selectedColor={selectedColor}
					onSelectColor={handleSelectColor}
					isColorPickerOpen={isColorPickerOpen}
					onColorPickerToggle={handleColorPickerToggle}
					onColorPickerClose={handleColorPickerClose}
					onPopupClose={handleContentStatePopupClose}
					onSubmit={handleSubmit}
					onCancel={handleCancel}
					onRemove={handleRemove}
					onFocusTextField={handleFocusTextField}
					onMouseEnterRemove={handleMouseEnterRemove}
					onMouseLeaveRemove={handleMouseLeaveRemove}
					userOffline={userOffline}
					contentStateButtonRef={contentStateButtonRef}
					onUpdateSuccess={onUpdateSuccess}
					isContentStateMenuOpen={isContentStateMenuOpen}
					setIsContentStateMenuOpen={setIsContentStateMenuOpenWithListener}
					updatePopup={update}
				/>
			)}
		/>
	);
};
export const EditorContentStateComponent = withFlags(EditorContentStateComponentImpl);
